import React, { useState, useEffect } from 'react';
import { useAuth } from '@/core/auth';
import { Button } from '@/ui/components/atoms/Button';
import {
  PlusIcon,
  ClockIcon,
  PlayIcon,
  PauseIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAutomatisations } from './hooks/useAutomatisation';
import { AutomatisationList } from './components/AutomatisationList';
import { AutomatisationForm } from './components/AutomatisationForm';
import { AutomatisationStats } from './components/AutomatisationStats';
import { ExecutionHistory } from './components/ExecutionHistory';
import { Automation } from './types/automatisation.types';
import { tasksAPI } from '../mes-taches/api/tasks.api';
import './styles/AutomatisationTab.css';

export const AutomatisationTab: React.FC = () => {
  const { user } = useAuth();
  const {
    automations,
    stats,
    isLoading,
    statsLoading,
    createAutomation,
    updateAutomation,
    deleteAutomation,
    toggleAutomation,
    executeAutomation,
    isCreating,
    isUpdating
  } = useAutomatisations();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all');
  const [users, setUsers] = useState<any[]>([]);

  // Fetch users for assignment
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await tasksAPI.getAllUsers();
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      }
    };
    fetchUsers();
  }, []);

  // Filter automations based on search and status
  const filteredAutomations = automations.filter(automation => {
    const matchesSearch = automation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          automation.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || automation.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleCreateAutomation = () => {
    setEditingAutomation(null);
    setIsFormOpen(true);
  };

  const handleEditAutomation = (automation: Automation) => {
    setEditingAutomation(automation);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (data: any) => {
    if (editingAutomation) {
      updateAutomation({ id: editingAutomation.id, data });
    } else {
      createAutomation(data);
    }
    setIsFormOpen(false);
    setEditingAutomation(null);
  };

  const handleViewHistory = (automation: Automation) => {
    setSelectedAutomation(automation);
    setShowHistory(true);
  };

  return (
    <div className="admin-automatisation-container">
      <div className="admin-automatisation-header">
        <div className="admin-automatisation-header-content">
          <div className="admin-automatisation-title-section">
            <h1 className="admin-automatisation-title">
              <ClockIcon className="admin-automatisation-title-icon" />
              Automatisations
            </h1>
            <p className="admin-automatisation-subtitle">
              Gérez vos tâches récurrentes et automatisées
            </p>
          </div>
          <Button
            onClick={handleCreateAutomation}
            variant="primary"
            className="admin-automatisation-create-btn"
          >
            <PlusIcon className="admin-automatisation-btn-icon" />
            Nouvelle automatisation
          </Button>
        </div>

        {/* Statistics */}
        {stats && (
          <AutomatisationStats stats={stats} loading={statsLoading} />
        )}

        {/* Filters */}
        <div className="admin-automatisation-filters">
          <div className="admin-automatisation-search">
            <MagnifyingGlassIcon className="admin-automatisation-search-icon" />
            <input
              type="text"
              placeholder="Rechercher une automatisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-automatisation-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="admin-automatisation-clear-btn"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                <XMarkIcon className="admin-automatisation-clear-icon" />
              </button>
            )}
          </div>
          <div className="admin-automatisation-status-filter">
            <button
              className={`admin-automatisation-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              Toutes ({automations.length})
            </button>
            <button
              className={`admin-automatisation-filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
              onClick={() => setFilterStatus('active')}
            >
              <PlayIcon className="admin-automatisation-filter-icon" />
              Actives ({automations.filter(a => a.status === 'active').length})
            </button>
            <button
              className={`admin-automatisation-filter-btn ${filterStatus === 'paused' ? 'active' : ''}`}
              onClick={() => setFilterStatus('paused')}
            >
              <PauseIcon className="admin-automatisation-filter-icon" />
              En pause ({automations.filter(a => a.status === 'paused').length})
            </button>
          </div>
        </div>
      </div>

      <div className="admin-automatisation-content">
        {isLoading ? (
          <div className="admin-automatisation-loading">
            <div className="admin-automatisation-spinner">Chargement...</div>
          </div>
        ) : filteredAutomations.length === 0 ? (
          <div className="admin-automatisation-empty">
            <ClockIcon className="admin-automatisation-empty-icon" />
            <h3 className="admin-automatisation-empty-title">
              {searchTerm || filterStatus !== 'all' 
                ? 'Aucune automatisation trouvée' 
                : 'Aucune automatisation créée'}
            </h3>
            <p className="admin-automatisation-empty-text">
              {searchTerm || filterStatus !== 'all'
                ? 'Essayez de modifier vos critères de recherche'
                : 'Créez votre première automatisation pour commencer'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button
                onClick={handleCreateAutomation}
                variant="primary"
                className="admin-automatisation-empty-btn"
              >
                <PlusIcon className="admin-automatisation-btn-icon" />
                Créer une automatisation
              </Button>
            )}
          </div>
        ) : (
          <AutomatisationList
            automations={filteredAutomations}
            onEdit={handleEditAutomation}
            onDelete={deleteAutomation}
            onToggle={toggleAutomation}
            onExecute={executeAutomation}
            onViewHistory={handleViewHistory}
          />
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <AutomatisationForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingAutomation(null);
          }}
          onSubmit={handleFormSubmit}
          initialData={editingAutomation}
          users={users}
          isLoading={isCreating || isUpdating}
        />
      )}

      {/* History Modal */}
      {showHistory && selectedAutomation && (
        <ExecutionHistory
          isOpen={showHistory}
          onClose={() => {
            setShowHistory(false);
            setSelectedAutomation(null);
          }}
          automation={selectedAutomation}
        />
      )}
    </div>
  );
};